import { useState, useEffect, useMemo, useRef } from 'react';
import { Eye, EyeOff, CheckCircle, XCircle, Loader2, Check, X } from 'lucide-react';
import { tokens } from '../theme/tokens';
import {
  scorePassword,
  getStrengthLevel,
  checkRequirements,
  STRENGTH_LABELS,
  STRENGTH_FILLED_BARS,
  type StrengthLevel,
} from '../utils/passwordStrength';
import { sanitizeReturnTo } from '../utils/returnTo';
import { isInvalidToken } from '../utils/invalidToken';
import { buildNativeResetLink } from '../utils/nativeResetLink';

const APP_SCHEMES: Record<string, { scheme: string; name: string }> = {
  qr: { scheme: 'cittam://', name: 'CITTAM QR' },
};

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.cittam.com';

const REDIRECT_COUNTDOWN_SECONDS = 3;

type Mode = 'form' | 'success' | 'recovery-no-token' | 'recovery-expired';
type RecoverySubmitState = 'idle' | 'submitting' | 'sent';

const STRENGTH_COLORS: Record<StrengthLevel, string> = {
  veryWeak: tokens.semantic.error,
  weak: tokens.semantic.error,
  fair: tokens.semantic.warning,
  good: tokens.semantic.warning,
  strong: tokens.semantic.success,
};

function isMobileUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

export default function ResetPassword() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const tokenParam = params.get('token');
  const errorParam = params.get('error');
  const appParam = params.get('app');
  const returnTo = sanitizeReturnTo(params.get('returnTo'));

  const appConfig = appParam && APP_SCHEMES[appParam] ? APP_SCHEMES[appParam] : APP_SCHEMES.qr;

  const initialMode: Mode = !tokenParam
    ? 'recovery-no-token'
    : errorParam === 'INVALID_TOKEN'
      ? 'recovery-expired'
      : 'form';

  const [mode, setMode] = useState<Mode>(initialMode);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Mobile redirect banner state
  const [redirectActive, setRedirectActive] = useState<boolean>(
    initialMode === 'form' && isMobileUA()
  );
  const [countdown, setCountdown] = useState<number>(REDIRECT_COUNTDOWN_SECONDS);
  const redirectTimerRef = useRef<number | null>(null);

  // Recovery (email) state
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryState, setRecoveryState] = useState<RecoverySubmitState>('idle');
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  const passwordRef = useRef<HTMLInputElement | null>(null);
  const successHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const recoveryEmailRef = useRef<HTMLInputElement | null>(null);

  const strengthScore = scorePassword(password);
  const strengthLevel = getStrengthLevel(strengthScore);
  const requirements = checkRequirements(password);
  const nativeResetLink = useMemo(
    () => (tokenParam ? buildNativeResetLink(appConfig.scheme, tokenParam, returnTo) : null),
    [appConfig.scheme, tokenParam, returnTo]
  );

  // Mobile redirect countdown effect
  useEffect(() => {
    if (!redirectActive || mode !== 'form' || !nativeResetLink) return;

    if (countdown <= 0) {
      window.location.href = nativeResetLink;
      return;
    }

    redirectTimerRef.current = window.setTimeout(() => {
      setCountdown((c) => c - 1);
    }, 1000);

    return () => {
      if (redirectTimerRef.current != null) window.clearTimeout(redirectTimerRef.current);
    };
  }, [redirectActive, countdown, nativeResetLink, mode]);

  // Autofocus management
  useEffect(() => {
    if (mode === 'form' && !redirectActive) passwordRef.current?.focus();
    if (mode === 'success') successHeadingRef.current?.focus();
    if (mode === 'recovery-no-token' || mode === 'recovery-expired')
      recoveryEmailRef.current?.focus();
  }, [mode, redirectActive]);

  const dismissRedirect = () => {
    if (redirectTimerRef.current != null) window.clearTimeout(redirectTimerRef.current);
    setRedirectActive(false);
    setCountdown(REDIRECT_COUNTDOWN_SECONDS);
  };

  const retryRedirect = () => {
    setCountdown(REDIRECT_COUNTDOWN_SECONDS);
    setRedirectActive(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    if (!tokenParam) {
      setFormError('Invalid or missing reset token');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenParam, newPassword: password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (isInvalidToken(data)) {
          setMode('recovery-expired');
          return;
        }
        const message =
          (typeof data?.message === 'string' && data.message) ||
          'Something went wrong. Please try again.';
        setFormError(message);
        return;
      }

      setMode('success');
    } catch {
      setFormError('Network error. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError(null);

    const email = recoveryEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setRecoveryError('Please enter a valid email address');
      return;
    }

    setRecoveryState('submitting');

    // Rebuild redirectTo with URLSearchParams, preserving sanitized returnTo.
    const redirectUrl = new URL(window.location.origin + '/reset-password');
    redirectUrl.searchParams.set('app', appConfig === APP_SCHEMES.qr ? 'qr' : (appParam ?? 'qr'));
    if (returnTo) redirectUrl.searchParams.set('returnTo', returnTo);

    try {
      await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase(),
          redirectTo: redirectUrl.toString(),
        }),
      });
      setRecoveryState('sent');
    } catch {
      setRecoveryError('Network error. Please check your connection.');
      setRecoveryState('idle');
    }
  };

  // Mobile redirect banner (sits on top of the form so the form is instant on dismiss)
  const redirectBanner =
    mode === 'form' && tokenParam && redirectActive ? (
      <div role="status" aria-live="polite" style={styles.banner}>
        <p style={styles.bannerTitle}>Opening {appConfig.name}…</p>
        <p style={styles.bannerSubtitle}>
          {countdown > 0 ? `Redirecting in ${countdown}s` : 'Attempting to open the app…'}
        </p>
        <div style={styles.bannerActions}>
          <button type="button" style={styles.linkButton} onClick={dismissRedirect}>
            Reset on web instead
          </button>
          {countdown <= 0 && (
            <button type="button" style={styles.linkButton} onClick={retryRedirect}>
              Try opening app again
            </button>
          )}
        </div>
      </div>
    ) : null;

  // ─── Success ────────────────────────────────────────────────────────
  if (mode === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <CheckCircle size={64} color={tokens.semantic.success} style={{ marginBottom: 24 }} />
          <h1 ref={successHeadingRef} tabIndex={-1} style={styles.title} aria-live="polite">
            Password updated
          </h1>
          <p style={styles.subtitle}>
            Return to the {appConfig.name} app on your phone and sign in with your new password.
          </p>
          <a href={appConfig.scheme} style={styles.button}>
            Open {appConfig.name} app
          </a>
          <p style={styles.hint}>On a computer? You're done — open the app on your phone.</p>
        </div>
      </div>
    );
  }

  // ─── Recovery (no token / expired) ──────────────────────────────────
  if (mode === 'recovery-no-token' || mode === 'recovery-expired') {
    const heading = mode === 'recovery-expired' ? 'This link expired' : 'Invalid reset link';
    const body =
      mode === 'recovery-expired'
        ? 'Reset links expire after 1 hour. Enter your email below and we’ll send a new one.'
        : 'This link is invalid. Enter your email below to request a new one.';

    if (recoveryState === 'sent') {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <CheckCircle size={56} color={tokens.semantic.success} style={{ marginBottom: 20 }} />
            <h1 style={styles.title} aria-live="polite">
              Check your email
            </h1>
            <p style={styles.subtitle}>
              If an account exists for that email, you’ll receive a new reset link shortly.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <XCircle size={56} color={tokens.semantic.error} style={{ marginBottom: 20 }} />
          <h1 style={styles.title}>{heading}</h1>
          <p style={styles.subtitle}>{body}</p>

          <form onSubmit={handleRecoverySubmit} style={styles.form}>
            {recoveryError && (
              <div role="alert" aria-live="assertive" style={styles.error}>
                {recoveryError}
              </div>
            )}
            <div style={styles.inputGroup}>
              <label htmlFor="recovery-email" style={styles.label}>
                Email
              </label>
              <input
                ref={recoveryEmailRef}
                id="recovery-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={recoveryEmail}
                onChange={(e) => {
                  setRecoveryEmail(e.target.value);
                  if (recoveryError) setRecoveryError(null);
                }}
                placeholder="you@example.com"
                style={styles.input}
                disabled={recoveryState === 'submitting'}
              />
            </div>
            <button
              type="submit"
              style={{
                ...styles.button,
                opacity: recoveryState === 'submitting' || !recoveryEmail ? 0.6 : 1,
              }}
              disabled={recoveryState === 'submitting' || !recoveryEmail}
            >
              {recoveryState === 'submitting' ? (
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                'Send new reset link'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Reset form (with optional redirect banner on top) ─────────────
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {redirectBanner}

        <h1 style={styles.title}>Reset Password</h1>
        <p style={styles.subtitle}>Set a new password for your {appConfig.name} account</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {formError && (
            <div role="alert" aria-live="assertive" style={styles.error}>
              {formError}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label htmlFor="new-password" style={styles.label}>
              New Password
            </label>
            <div style={styles.inputWrapper}>
              <input
                ref={passwordRef}
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (formError) setFormError(null);
                }}
                placeholder="Enter new password"
                style={styles.input}
                disabled={submitting}
                autoComplete="new-password"
                aria-describedby="strength-meter requirements-list"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={styles.toggleButton}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <StrengthMeter score={strengthScore} level={strengthLevel} />
          <RequirementsList requirements={requirements} />

          <div style={styles.inputGroup}>
            <label htmlFor="confirm-password" style={styles.label}>
              Confirm Password
            </label>
            <div style={styles.inputWrapper}>
              <input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (formError) setFormError(null);
                }}
                placeholder="Confirm new password"
                style={styles.input}
                disabled={submitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                style={styles.toggleButton}
                aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !password || !confirmPassword}
            style={{
              ...styles.button,
              opacity: submitting || !password || !confirmPassword ? 0.6 : 1,
            }}
          >
            {submitting ? (
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function StrengthMeter({ score, level }: { score: number; level: StrengthLevel }) {
  const filled = STRENGTH_FILLED_BARS[level];
  const color = STRENGTH_COLORS[level];
  return (
    <div id="strength-meter" style={styles.strengthWrapper} aria-live="polite">
      <div style={styles.strengthBars}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              ...styles.strengthBar,
              background: i < filled ? color : 'rgba(148, 163, 184, 0.2)',
            }}
          />
        ))}
      </div>
      <span style={{ ...styles.strengthLabel, color: score > 0 ? color : tokens.text.muted }}>
        {score > 0 ? STRENGTH_LABELS[level] : 'Strength'}
      </span>
    </div>
  );
}

function RequirementsList({
  requirements,
}: {
  requirements: ReturnType<typeof checkRequirements>;
}) {
  const items: { key: keyof typeof requirements; label: string }[] = [
    { key: 'length', label: '8+ characters' },
    { key: 'number', label: 'At least one number' },
    { key: 'mixedCase', label: 'Upper and lower case' },
    { key: 'symbol', label: 'At least one symbol' },
  ];
  return (
    <ul id="requirements-list" style={styles.requirementsList} aria-label="Password requirements">
      {items.map(({ key, label }) => {
        const met = requirements[key];
        return (
          <li key={key} style={styles.requirementItem}>
            {met ? (
              <Check size={14} color={tokens.semantic.success} aria-hidden />
            ) : (
              <X size={14} color={tokens.text.muted} aria-hidden />
            )}
            <span
              style={{
                ...styles.requirementText,
                color: met ? tokens.semantic.success : tokens.text.muted,
              }}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    maxWidth: 420,
    margin: '0 auto',
  },
  card: {
    background: tokens.bg.card,
    backdropFilter: 'blur(12px)',
    borderRadius: tokens.radius.xl,
    padding: tokens.spacing['2xl'],
    border: `1px solid ${tokens.border.default}`,
    textAlign: 'center',
  },
  banner: {
    background: 'rgba(20, 184, 166, 0.08)',
    border: `1px solid ${tokens.brand.primary}`,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.lg,
    marginBottom: tokens.spacing.xl,
    textAlign: 'left',
  },
  bannerTitle: {
    color: tokens.text.primary,
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 4,
  },
  bannerSubtitle: {
    color: tokens.text.secondary,
    fontSize: 13,
    marginBottom: tokens.spacing.sm,
  },
  bannerActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacing.md,
  },
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: tokens.brand.primary,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 8,
    color: tokens.text.primary,
  },
  subtitle: {
    fontSize: 14,
    color: tokens.text.muted,
    marginBottom: tokens.spacing['2xl'],
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.lg,
  },
  inputGroup: {
    textAlign: 'left',
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 500,
    color: tokens.text.secondary,
    marginBottom: tokens.spacing.sm,
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: 16,
    background: 'rgba(15, 23, 42, 0.6)',
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.md,
    color: tokens.text.primary,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  toggleButton: {
    position: 'absolute',
    right: 12,
    background: 'none',
    border: 'none',
    color: tokens.text.muted,
    cursor: 'pointer',
    padding: 4,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 24px',
    fontSize: 16,
    fontWeight: 600,
    color: '#ffffff',
    background: `linear-gradient(135deg, ${tokens.brand.primary} 0%, ${tokens.brand.primaryDark} 100%)`,
    border: 'none',
    borderRadius: tokens.radius.md,
    cursor: 'pointer',
    textDecoration: 'none',
    marginTop: tokens.spacing.sm,
    transition: 'transform 0.2s, opacity 0.2s',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    color: tokens.semantic.error,
    fontSize: 14,
    textAlign: 'left',
  },
  hint: {
    fontSize: 12,
    color: tokens.text.muted,
    marginTop: tokens.spacing.lg,
  },
  strengthWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  strengthBars: {
    display: 'flex',
    flex: 1,
    gap: 4,
  },
  strengthBar: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    transition: 'background 0.2s',
  },
  strengthLabel: {
    fontSize: 12,
    fontWeight: 600,
    minWidth: 80,
    textAlign: 'right',
  },
  requirementsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
  },
  requirementItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  requirementText: {
    fontSize: 12,
  },
};
